import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { INTERVIEW_TYPES, type Interview, type InterviewOutcome, type InterviewStatus, type InterviewType } from "@/types";

const TYPE_LABELS: Record<InterviewType, string> = {
  phone: "Phone Screen",
  video: "Video Call",
  onsite: "On-Site",
  technical: "Technical",
  behavioral: "Behavioral",
  final: "Final Round",
};

const STATUS_STYLES: Record<InterviewStatus, string> = {
  scheduled: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-surface-secondary text-text-secondary",
  no_show: "bg-rose-100 text-error-text",
};

const OUTCOME_STYLES: Record<NonNullable<InterviewOutcome>, string> = {
  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fail: "bg-rose-50 text-error-text border-rose-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
};

function formatDateTime(s: string): string {
  return new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function CandidateInterviews({ candidateId }: { candidateId: number }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.interviews(candidateId),
    queryFn: () => api.listInterviews(candidateId),
  });
  const interviews: Interview[] = data?.interviews ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.interviews(candidateId) });

  const upcoming = interviews.filter((i) => i.status === "scheduled");
  const past = interviews.filter((i) => i.status !== "scheduled");

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-near-black">Interviews ({interviews.length})</h3>

      {isLoading ? (
        <p className="text-xs text-text-secondary">Loading…</p>
      ) : interviews.length === 0 ? (
        <p className="text-xs italic text-text-secondary">No interviews scheduled yet.</p>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wide text-text-secondary">Upcoming</h4>
              {upcoming.map((i) => (
                <InterviewCard key={i.id} interview={i} onUpdated={refresh} />
              ))}
            </div>
          )}
          {past.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wide text-text-secondary">Past</h4>
              {past.map((i) => (
                <InterviewCard key={i.id} interview={i} onUpdated={refresh} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InterviewCard({ interview, onUpdated }: { interview: Interview; onUpdated: () => void }) {
  const [editing, setEditing] = useState(false);

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateInterview>[1]) => api.updateInterview(interview.id, patch),
    onSuccess: onUpdated,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteInterview(interview.id),
    onSuccess: onUpdated,
  });

  const interviewer = interview.interviewer_display_name || interview.interviewer_username || interview.interviewer_name || "—";

  if (editing) {
    return <InterviewEditForm interview={interview} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onUpdated(); }} />;
  }

  return (
    <div className="rounded-card bg-white p-3 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-near-black">{TYPE_LABELS[interview.interview_type]}</span>
            <span className={`rounded-badge px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[interview.status]}`}>
              {interview.status.replace("_", " ")}
            </span>
            {interview.outcome && (
              <span className={`rounded-badge border px-2 py-0.5 text-[10px] font-semibold ${OUTCOME_STYLES[interview.outcome]}`}>
                {interview.outcome}
              </span>
            )}
          </div>
          <div className="mt-1 space-y-0.5 text-xs text-text-secondary">
            <p>{formatDateTime(interview.scheduled_at)} · {interview.duration_minutes} min</p>
            <p>Interviewer: <span className="text-text-focused">{interviewer}</span></p>
            {interview.location && <p>Location: <span className="text-text-focused">{interview.location}</span></p>}
            {interview.notes && <p className="mt-1 whitespace-pre-wrap italic text-text-focused">{interview.notes}</p>}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            onClick={() => setEditing(true)}
            className="rounded-btn px-2 py-0.5 text-[10px] font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
          >
            Edit
          </button>
          <button
            onClick={() => { if (confirm("Delete this interview?")) remove.mutate(); }}
            className="rounded-btn px-2 py-0.5 text-[10px] font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Quick status actions */}
      {interview.status === "scheduled" && (
        <div className="mt-2 flex flex-wrap gap-1.5 border-t border-surface-secondary pt-2">
          <button
            onClick={() => update.mutate({ status: "completed", outcome: "pass" })}
            className="rounded-btn bg-emerald-100 px-3 py-1 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-200"
          >
            ✓ Mark passed
          </button>
          <button
            onClick={() => update.mutate({ status: "completed", outcome: "fail" })}
            className="rounded-btn bg-rose-100 px-3 py-1 text-[10px] font-semibold text-error-text hover:bg-rose-200"
          >
            ✕ Mark failed
          </button>
          <button
            onClick={() => update.mutate({ status: "no_show" })}
            className="rounded-btn bg-amber-100 px-3 py-1 text-[10px] font-semibold text-amber-800 hover:bg-amber-200"
          >
            No-show
          </button>
          <button
            onClick={() => update.mutate({ status: "cancelled" })}
            className="rounded-btn bg-surface-secondary px-3 py-1 text-[10px] font-semibold text-text-secondary hover:bg-border-card"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function InterviewEditForm({
  interview,
  onCancel,
  onSaved,
}: {
  interview: Interview;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [scheduledAt, setScheduledAt] = useState(interview.scheduled_at.slice(0, 16));
  const [duration, setDuration] = useState(interview.duration_minutes);
  const [type, setType] = useState<InterviewType>(interview.interview_type);
  const [interviewerName, setInterviewerName] = useState(interview.interviewer_name || "");
  const [location, setLocation] = useState(interview.location || "");
  const [notes, setNotes] = useState(interview.notes || "");
  const [outcome, setOutcome] = useState<InterviewOutcome | "">(interview.outcome || "");

  const save = useMutation({
    mutationFn: () => api.updateInterview(interview.id, {
      scheduled_at: new Date(scheduledAt).toISOString(),
      duration_minutes: duration,
      interview_type: type,
      interviewer_name: interviewerName,
      location,
      notes,
      outcome: outcome || undefined,
    }),
    onSuccess: onSaved,
  });

  return (
    <div className="rounded-card border border-rausch bg-white p-3 shadow-card">
      <div className="grid grid-cols-2 gap-2">
        <FieldInput label="Date & time" type="datetime-local" value={scheduledAt} onChange={setScheduledAt} />
        <FieldInput label="Duration (min)" type="number" value={String(duration)} onChange={(v) => setDuration(Number(v) || 60)} />
        <div className="col-span-2">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">Type</label>
          <select
            className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
            value={type}
            onChange={(e) => setType(e.target.value as InterviewType)}
          >
            {INTERVIEW_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <FieldInput label="Interviewer" value={interviewerName} onChange={setInterviewerName} placeholder="Name" />
        <FieldInput label="Location / link" value={location} onChange={setLocation} placeholder="Zoom link or address" />
        <div className="col-span-2">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">Notes</label>
          <textarea
            className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        {interview.status === "completed" && (
          <div className="col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">Outcome</label>
            <select
              className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as InterviewOutcome | "")}
            >
              <option value="">—</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        )}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-btn px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-near-black">
          Cancel
        </button>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-btn bg-near-black px-3 py-1.5 text-xs font-medium text-white hover:bg-rausch disabled:opacity-50">
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

export function ScheduleInterviewDialog({
  candidateId,
  onClose,
  onCreated,
}: {
  candidateId: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [duration, setDuration] = useState(60);
  const [type, setType] = useState<InterviewType>("phone");
  const [interviewerName, setInterviewerName] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const save = useMutation({
    mutationFn: () => api.createInterview({
      candidate_id: candidateId,
      scheduled_at: new Date(scheduledAt).toISOString(),
      duration_minutes: duration,
      interview_type: type,
      interviewer_name: interviewerName || undefined,
      location: location || undefined,
      notes: notes || undefined,
    }),
    onSuccess: onCreated,
    onError: (e: any) => setError(e.message || "Failed"),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-full max-w-md rounded-card bg-white p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-lg font-semibold text-near-black">Schedule interview</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Type</label>
            <select
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={type}
              onChange={(e) => setType(e.target.value as InterviewType)}
            >
              {INTERVIEW_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldInput label="Date & time" type="datetime-local" value={scheduledAt} onChange={setScheduledAt} />
            <FieldInput label="Duration (min)" type="number" value={String(duration)} onChange={(v) => setDuration(Number(v) || 60)} />
          </div>
          <FieldInput label="Interviewer" value={interviewerName} onChange={setInterviewerName} placeholder="Name" />
          <FieldInput label="Location / link" value={location} onChange={setLocation} placeholder="Zoom link or address" />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Notes</label>
            <textarea
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agenda, prep notes, areas to cover…"
            />
          </div>
          {error && <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black">
            Cancel
          </button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white hover:bg-rausch disabled:opacity-50">
            {save.isPending ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
      />
    </div>
  );
}
